"use client";

import Link from "next/link";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { cn } from "@dionatanbig2be/big2be-ui/utils";
import { Icon } from "@/shared/components/Icon";
import { ScrollArrow } from "../ScrollArrow";
import { SCROLL_TRACK_CLASS, useHorizontalScroll } from "@/shared/hooks/useHorizontalScroll";
import type { CategoryNavItem, CategoryNavVariantProps } from "../../../types/category-nav";

const ARROW_CLASS = "text-primary/60 hover:text-primary";

/**
 * CategoryNav — variant-1: barra sem fundo (border-b), categorias centralizadas
 * em uppercase com cor primary e espaçamento largo. Sem "Departamentos"/painel.
 * Com `highlightItems` (destaques do portal), as abas exibem esses itens (com
 * ícone) no lugar das categorias raiz.
 */
export function CategoryNavVariant1({ categories, highlightItems }: CategoryNavVariantProps) {
    // Mesmo trilho da vitrine de produtos e da variant-default: touch nativo
    // com inércia, Shift + roda, setas medidas por ResizeObserver.
    const { ref, canLeft, canRight, update, scrollBy } = useHorizontalScroll({ step: 300 });

    const hasHighlights = Boolean(highlightItems?.length);
    const tabs: CategoryNavItem[] = hasHighlights ? highlightItems! : categories;

    if (tabs.length === 0) return null;

    return (
        <nav
            className="mb-6 w-full border-b border-base-200 bg-base-100"
            data-variant="variant1"
            aria-label="Categorias"
        >
            <div className="relative mx-auto max-w-7xl px-4">
                {canLeft && (
                    <ScrollArrow
                        side="left"
                        onClick={() => scrollBy("left")}
                        className={ARROW_CLASS}
                    >
                        <FiChevronLeft className="h-4 w-4" />
                    </ScrollArrow>
                )}

                <div ref={ref} onScroll={update} className={SCROLL_TRACK_CLASS}>
                    {/* `w-max mx-auto` e NÃO `justify-center`: margem auto
                        vira 0 quando o conteúdo estoura, então a lista centra
                        enquanto cabe e cola à esquerda quando rola. Com
                        `justify-center` o excedente sobra dos DOIS lados e o
                        começo da lista fica inalcançável — `scrollLeft` não
                        vai a negativo. */}
                    <ul className="mx-auto flex w-max items-center">
                        {tabs.map((cat) => (
                            <li key={cat.id} className="shrink-0">
                                <Link
                                    href={cat.href ?? "#"}
                                    className={cn(
                                        "flex items-center gap-2 whitespace-nowrap px-8 text-sm font-semibold uppercase tracking-wide transition-colors",
                                        cat.featured
                                            ? "mx-1 my-1 rounded-lg py-2 text-primary [--nav-featured-color:var(--color-primary)] animate-nav-featured-blink motion-reduce:animate-none motion-reduce:bg-primary/10"
                                            : "py-3 text-primary/80 hover:text-primary"
                                    )}
                                >
                                    {hasHighlights && (
                                        <Icon name={cat.icon} size={16} className="shrink-0" />
                                    )}
                                    {cat.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {canRight && (
                    <ScrollArrow
                        side="right"
                        onClick={() => scrollBy("right")}
                        className={ARROW_CLASS}
                    >
                        <FiChevronRight className="h-4 w-4" />
                    </ScrollArrow>
                )}
            </div>
        </nav>
    );
}
